import React, { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'react-feather';
import { Link } from 'react-router-dom';
import api from '../../config/axios';
import Breadcrumb from '../common/breadcrumb';
import Loader from '../common/loader';
import { date, time } from '../../utils/date';

const Conversation = (props) => {
    const { phone } = props.match.params;
    const [loading, setLoading] = useState(true);
    const [interactions, setInteractions] = useState([]);
    const [client, setClient] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 50;

    useEffect(() => {
        fetchConversation(1);
    }, [phone]);

    const fetchConversation = async (pageNum) => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/whatsapp/logs/conversation/${phone}?limit=${limit}&page=${pageNum}`
            );
            setInteractions(data.interactions || []);
            setClient(data.client || null);
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
            setPage(pageNum);
        } catch (err) {
            toast.error('Error al cargar la conversación');
        } finally {
            setLoading(false);
        }
    };

    // ── Parsear content ──
    const parseContent = (item) => {
        if (!item.content) return '-';
        if (item.direction === 'outbound' && item.messageType === 'interactive') {
            try {
                const parsed = JSON.parse(item.content);
                if (parsed.body && parsed.body.text) return parsed.body.text;
                return item.content;
            } catch {
                return item.content;
            }
        }
        return item.content;
    };

    // ── Badge source ──
    const sourceBadge = (source) => {
        if (source === 'bot') return <span className="badge badge-secondary" style={{ fontSize: 10 }}>Bot</span>;
        if (source === 'dashboard') return <span className="badge badge-warning" style={{ fontSize: 10 }}>Dashboard</span>;
        return null;
    };

    // ── Paginación ──
    const handlePrev = () => {
        const prev = page - 1;
        if (prev >= 1) fetchConversation(prev);
    };

    const handleNext = () => {
        const next = page + 1;
        if (next <= totalPages) fetchConversation(next);
    };

    return (
        <Fragment>
            <Breadcrumb title="🤖 Conversación del Bot" parent={{ href: '/bitacora-bot', title: 'Bitácora' }} />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <Link to="/bitacora-bot" className="btn btn-sm btn-outline-secondary">
                                            <ArrowLeft size={14} className="mr-1" />
                                            Volver
                                        </Link>
                                    </div>
                                    <div className="text-right">
                                        <h5 className="mb-0">📞 {phone}</h5>
                                        {client && (
                                            <p className="text-muted mb-0">
                                                {client.name} · ID: {client.id}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Loading */}
                                {loading && (
                                    <div style={{ display: 'flex', height: '50vh' }}>
                                        <Loader />
                                    </div>
                                )}

                                {/* Sin datos */}
                                {!loading && interactions.length === 0 && (
                                    <div className="text-center py-5">
                                        <p style={{ fontSize: 18, color: '#777' }}>
                                            No hay interacciones registradas para este teléfono
                                        </p>
                                    </div>
                                )}

                                {/* Chat */}
                                {!loading && interactions.length > 0 && (
                                    <Fragment>
                                        <div
                                            style={{
                                                maxHeight: '60vh',
                                                overflowY: 'auto',
                                                padding: '15px',
                                                background: '#f5f5f5',
                                                borderRadius: 8,
                                            }}
                                        >
                                            {[...interactions].reverse().map((item, idx) => {
                                                const isInbound = item.direction === 'inbound';
                                                return (
                                                    <div
                                                        key={item.id || idx}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: isInbound ? 'flex-start' : 'flex-end',
                                                            marginBottom: 12,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                maxWidth: '70%',
                                                                padding: '10px 15px',
                                                                borderRadius: 12,
                                                                background: isInbound ? '#fff' : '#00a4d3',
                                                                color: isInbound ? '#333' : '#fff',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                            }}
                                                        >
                                                            {/* Metadata line */}
                                                            <div style={{ fontSize: 10, marginBottom: 4, opacity: 0.7 }}>
                                                                {time(item.createdAt)} · {item.messageType}
                                                                {item.source && ` · `}
                                                                {item.source && sourceBadge(item.source)}
                                                            </div>
                                                            {/* Content */}
                                                            <div style={{ fontSize: 14, wordBreak: 'break-word' }}>
                                                                {parseContent(item)}
                                                            </div>
                                                            {/* Action badge */}
                                                            {item.action && (
                                                                <div style={{ marginTop: 4 }}>
                                                                    <span
                                                                        style={{
                                                                            fontSize: 10,
                                                                            background: isInbound ? '#e0e0e0' : 'rgba(255,255,255,0.2)',
                                                                            padding: '2px 8px',
                                                                            borderRadius: 8,
                                                                        }}
                                                                    >
                                                                        ⚡ {item.action}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {/* Purchase link */}
                                                            {item.purchaseId && (
                                                                <div style={{ marginTop: 4 }}>
                                                                    <Link
                                                                        to={`/recargas/detalle/${item.purchaseId}`}
                                                                        style={{
                                                                            fontSize: 10,
                                                                            color: isInbound ? '#00a4d3' : '#fff',
                                                                            textDecoration: 'underline',
                                                                        }}
                                                                    >
                                                                        Ver pedido #{item.purchaseId}
                                                                    </Link>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Paginación */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <span className="text-muted">
                                                {total} mensajes · Página {page} de {totalPages}
                                            </span>
                                            <div>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary mr-2"
                                                    onClick={handlePrev}
                                                    disabled={page <= 1}
                                                >
                                                    ← Anterior
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={handleNext}
                                                    disabled={page >= totalPages}
                                                >
                                                    Siguiente →
                                                </button>
                                            </div>
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default Conversation;
